import React from "react";
import { motion } from "framer-motion";
import { Coins, Server, ShieldCheck, SlidersHorizontal } from "lucide-react";

const AICreditFAQSlide = () => {
  return (
    <div className="h-full w-full bg-[#f8fafc] flex flex-col items-center justify-center p-8 xl:p-12 relative overflow-hidden font-sans">
      {/* Ambient gradient blurs — blue scheme */}
      <div
        className="absolute top-[-200px] left-[-100px] w-[700px] h-[700px] rounded-full blur-[140px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #bfdbfe 0%, #c4b5fd 50%, #93c5fd 100%)" }}
      />
      <div
        className="absolute bottom-[-200px] right-[-100px] w-[700px] h-[700px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #a5f3fc 0%, #93c5fd 50%, #c4b5fd 100%)" }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_0.8px,transparent_0.8px)] [background-size:28px_28px] opacity-40 pointer-events-none" />

      <div className="w-full flex flex-col items-center relative z-10 max-w-[1200px]">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 text-blue-600 text-sm font-semibold mb-4 border border-blue-100/60 shadow-sm">
            <Coins className="w-4 h-4 text-blue-500" />
            <span>AI Credit Utilization</span>
          </div>
          <h2 className="text-3xl lg:text-[46px] font-extrabold text-slate-900 tracking-tight leading-tight">
            How we handle AI costs.{" "}
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              And why you're protected.
            </span>
          </h2>
        </motion.div>

        {/* Content blocks */}
        <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Why Credits Exist */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="rounded-[24px] overflow-hidden border border-white shadow-[0_20px_60px_-16px_rgba(0,0,0,0.08)] bg-white/80 backdrop-blur-xl relative p-8 flex flex-col"
          >
            <div className="absolute top-0 left-0 right-0 h-[4px]" style={{ background: "linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)" }} />
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mb-6 shadow-lg shadow-blue-500/20 border border-blue-400 shrink-0">
              <Server className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-[20px] font-bold text-slate-900 mb-4">
              Why do AI credits exist?
            </h3>
            <p className="text-[14px] text-slate-500 leading-relaxed font-medium">
              Enterprise-grade AI is very expensive to run. Every time our agents process calls, understand documents, or reason through denials, we are charged based on the compute tokens the models consume.
              <br /><br />
              Credits ensure we don't lose money on contracts for practices with exceptionally high volumes, while still providing massive baseline allocations that cover the vast majority of clinics.
            </p>
          </motion.div>

          {/* AI Utilization Management */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="rounded-[24px] overflow-hidden border border-white shadow-[0_20px_60px_-16px_rgba(0,0,0,0.08)] bg-white/80 backdrop-blur-xl relative p-8 flex flex-col"
          >
            <div className="absolute top-0 left-0 right-0 h-[4px]" style={{ background: "linear-gradient(90deg, #6366f1 0%, #4f46e5 100%)" }} />
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center mb-6 shadow-lg shadow-indigo-500/20 border border-indigo-400 shrink-0">
              <SlidersHorizontal className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-[20px] font-bold text-slate-900 mb-4">
              AI Utilization Management
            </h3>
            <p className="text-[14px] text-slate-500 leading-relaxed font-medium">
              While we don't anticipate overages for most offices, our system includes a built-in AI utilization management tool.
              <br /><br />
              This dashboard provides complete transparency, allowing you to monitor token usage in real-time, set automated limits, and effortlessly manage AI costs responsibly across your entire workforce.
            </p>
          </motion.div>

          {/* ROI Guarantee */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="rounded-[24px] overflow-hidden border border-white shadow-[0_20px_60px_-16px_rgba(0,0,0,0.08)] bg-white/80 backdrop-blur-xl relative p-8 flex flex-col"
          >
            <div className="absolute top-0 left-0 right-0 h-[4px]" style={{ background: "linear-gradient(90deg, #10b981 0%, #059669 100%)" }} />
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center mb-6 shadow-lg shadow-emerald-500/20 border border-emerald-400 shrink-0">
              <ShieldCheck className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-[20px] font-bold text-slate-900 mb-4">
              The ROI Guarantee
            </h3>
            <p className="text-[14px] text-slate-500 leading-relaxed font-medium">
              We stand behind our platform's ability to drive growth. 
              <br /><br />
              If Asha does not drive more ROI (tracked automatically via our ROI tracking system) relative to what you pay us, <strong className="text-emerald-600">we will swallow the costs for any AI overages</strong>. You are fully protected — you only pay overages if the system is overwhelmingly profitable for your practice.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AICreditFAQSlide;
